import {useMemo, useRef} from 'react';
import {getMeetingsByDate, useAppSelector} from '@lib/store';
import {dateKeyForDateTimeString} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {trackEvent} from '@lib/infrastructure';
import {useMemoDate} from '@lib/store/src/ui/hooks/useMemoDate';
import {LocalTimespan, trySuggestShortenedReservation, constructAppInsightsEvent} from './timeAdjustmentHelpers';
import {TimeslotsEvents} from '@lib/infrastructure/src/infrastructure/analytics/actions/types/TimeslotsEvents';

export const useTimeAdjustmentSuggestion = (currentTimespan: LocalTimespan, initialTimespan: LocalTimespan) => {
  const dateKey = dateKeyForDateTimeString(currentTimespan.localStart);
  const date = useMemoDate(dateKey);
  const allMeetings = useAppSelector((state) => getMeetingsByDate(state, date));
  const meetings = useMemo(() => {
    // TODO: only use meetings where user RSVPd with attending
    return allMeetings.filter((m) => m.attendees.length > 1 && !m.isAllDay);
  }, [allMeetings]);

  const suggestion = useMemo(() => {
    return trySuggestShortenedReservation(currentTimespan, initialTimespan, meetings);
  }, [
    currentTimespan.localStart,
    currentTimespan.localEnd,
    initialTimespan.localStart,
    initialTimespan.localEnd,
    meetings,
  ]);

  // Only trigger presented events once during the lifecycle of the component (instead of, e.g., on every boolean hidden flip)
  const suggestionsPresented = useRef(new Set<string>());
  const tryTriggerTrackingEvent = (
    mutation: TimeslotsEvents['Timeslots_WorkspaceMeetingOverlapSuggestionInteraction']['mutation'],
    action: TimeslotsEvents['Timeslots_WorkspaceMeetingOverlapSuggestionInteraction']['action'],
  ) => {
    if (!suggestion) return null;

    const event = constructAppInsightsEvent(meetings, suggestion, initialTimespan, mutation, action);

    if (event.action === 'presented') {
      const hash = JSON.stringify(event);
      if (suggestionsPresented.current.has(hash)) return;

      suggestionsPresented.current.add(hash);
    }

    trackEvent('Timeslots_WorkspaceMeetingOverlapSuggestionInteraction', event);
  };

  return {suggestion, tryTriggerTrackingEvent};
};
